import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import IconRight from '@/assets/icons/icon-right.svg';
import { HiringGoalModal } from '@/components/pages/channels/hiring-goals-modal';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useOpeningDetails } from '@/contexts/opening-context';
import { formatShortDate } from '@/utils/date-util';

import { AggregateHiresControls } from './aggregate-hires-controls';
import { TimeframeInfo } from './timeframe-info';

export function HiringGoal() {
  const { hiringGoal } = useOpeningDetails();
  const intl = useIntl();
  const [isHiringGoalModalOpen, setIsHiringGoalModalOpen] = useState(false);

  const getProgress = (expected: number | null, actual: number | null) => {
    if (!actual || !expected) return 0;

    return (actual / expected) * 100;
  };

  if (!hiringGoal) return <span className="h-10" />;

  const {
    start_date: startDate,
    hire_by: endDate,
    days_elapsed: daysElapsed,
    hire_stats: { actual, expected },
  } = hiringGoal;

  return (
    <div className="flex w-full items-start justify-between gap-4 lg:w-fit lg:justify-center">
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-start gap-4">
          <TimeframeInfo
            title={intl.formatMessage({
              defaultMessage: 'Start date',
              description:
                'Label on hiring goal details for when the period begins',
            })}
          >
            <Typography variant="body2">
              {formatShortDate(startDate)}
            </Typography>
          </TimeframeInfo>

          <TimeframeInfo
            title={intl.formatMessage({
              defaultMessage: 'End date',
              description:
                'Label on hiring goal details for when the period ends',
            })}
          >
            {endDate ? (
              <Typography variant="body2">
                {formatShortDate(endDate)}
              </Typography>
            ) : (
              <Typography variant="body2" asChild>
                <Link
                  to={hiringGoal.url}
                  target="_blank"
                  className="text-fx-primary underline underline-offset-4 hover:underline"
                >
                  <FormattedMessage
                    defaultMessage="+ Add end date"
                    description="Link to add an end date to the hiring goal"
                  />
                </Link>
              </Typography>
            )}
          </TimeframeInfo>
        </div>
        <Typography
          variant="small1"
          className="text-fx-neutral-600 whitespace-nowrap"
        >
          <FormattedMessage
            defaultMessage="Elapsed: {count, plural, one {# day} other {# days}}"
            description="Elapsed days in the hiring goal; {count} is the number of days"
            values={{ count: daysElapsed }}
          />
        </Typography>
      </div>

      <div className="flex w-[370px] flex-col items-start gap-2 pl-4">
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Hiring target"
            description="Caption above the hire count progress for this goal"
          />
        </Typography>
        <div className="flex w-full items-center justify-between">
          <Typography variant="large2">
            {`${actual ?? '-'} of ${expected ?? '-'}`}
          </Typography>
          <Button
            variant="ghost"
            className="h-6 p-0 hover:bg-white"
            onClick={() => setIsHiringGoalModalOpen(true)}
          >
            <FormattedMessage
              defaultMessage="View All"
              description="Opens the full hiring goal modal from the compact header"
            />
            <img src={IconRight} alt="IconRight" />
          </Button>
        </div>

        <Progress
          value={getProgress(expected, actual)}
          className="bg-fx-primary"
        />

        <AggregateHiresControls />
      </div>
      <HiringGoalModal
        open={isHiringGoalModalOpen}
        onOpenChange={setIsHiringGoalModalOpen}
      />
    </div>
  );
}

HiringGoal.Skeleton = function HiringGoalSkeleton() {
  const intl = useIntl();
  return (
    <div className="flex items-start gap-4">
      <div className="flex flex-col items-start gap-2">
        <div className="flex items-start gap-4">
          <TimeframeInfo.Skeleton
            title={intl.formatMessage({
              defaultMessage: 'Start date',
              description:
                'Label on hiring goal details for when the period begins',
            })}
          />

          <TimeframeInfo.Skeleton
            title={intl.formatMessage({
              defaultMessage: 'End date',
              description:
                'Label on hiring goal details for when the period ends',
            })}
          />
        </div>
        <Skeleton className="h-4 w-36" />
      </div>

      <div className="flex w-[370px] flex-col items-start gap-2 pl-4">
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Hiring target"
            description="Caption above the hire count progress for this goal"
          />
        </Typography>
        <div className="flex w-full items-center justify-between">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-5 w-24" />
        </div>
        <Skeleton className="h-2 w-full" />
        <AggregateHiresControls.Skeleton />
      </div>
    </div>
  );
};
